/**
 * When to ask the backend to re-check a video's status against Bunny's live
 * API, and when to stop asking.
 *
 * Background: `bunny_status` is a BE column fed by a Bunny webhook. On
 * 2026-09-08 that webhook stream ran ~38 minutes behind Bunny's own live API,
 * so a creator watched "Processing" for 38 minutes on a video that had encoded
 * in 41 seconds. BACK-209 fixed the passive half of that (the reaper now syncs
 * in-flight progress every 5 min) and added an on-demand escape hatch,
 * `POST /videos/{uuid}/bunny-status/refresh`.
 *
 * This module decides when the client pulls that lever. Three rules shape it:
 *
 * 1. **Status alone is not a stall signal.** A video legitimately sits at
 *    status 2 for the whole encode. Only when neither the status nor
 *    `bunny_encode_progress` has moved is something actually stuck.
 * 2. **Stop at reaper parity.** The BE reaper closes the same gap every 5 min.
 *    Once our backoff reaches that, refreshing is strictly wasted work — the
 *    cron gets there first — so escalation terminates rather than growing.
 * 3. **Jitter, always.** A bulk upload puts many videos into processing within
 *    seconds of each other. Deterministic backoff makes them escalate in
 *    lockstep and hit Bunny in a burst; randomising each delay decorrelates
 *    them. This is the "full jitter" variant, which AWS measured as the best
 *    performing of the standard strategies.
 *    https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 */

/** No status and no encode-progress movement for this long ⇒ ask Bunny. */
const FIRST_REFRESH_AFTER_MS = 60_000;

/**
 * Ceiling on the backoff, and the point at which we give up entirely. Matches
 * the BE reaper's 5-minute cadence: a slower refresh cannot beat the cron, so
 * there is nothing left to buy.
 */
const REAPER_PARITY_MS = 5 * 60_000;

/** Doubling. Fibonacci was considered; over a 60s→300s span the growth base is
 * a rounding error next to jitter, and doubling is the more recognisable shape
 * for whoever maintains this next. */
const BACKOFF_FACTOR = 2;

export interface RefreshEscalation {
  /**
   * Milliseconds to wait before the next refresh, or `null` once escalation
   * has passed reaper parity and the cron should take over.
   */
  nextDelayMs: () => number | null;
  /**
   * A refresh was issued — advance the backoff. Returns `true` on the call that
   * exhausts escalation, so the caller can record the hand-off to the cron
   * exactly once. Without that edge the client simply goes quiet: production
   * showed a 24-minute window (2026-09-15, video `5e4ccb65`) with no telemetry
   * at all between the last refresh and the video turning ready, which reads
   * identically to a video nobody was watching.
   */
  onRefreshIssued: () => boolean;
  /** Status or encode progress moved — the video is alive, start over. */
  onProgressObserved: () => void;
  /** True once we have stopped refreshing this video. */
  isExhausted: () => boolean;
  snapshot: () => { step: number; exhausted: boolean };
}

export interface RefreshEscalationOptions {
  /** Injectable for deterministic tests. Defaults to `Math.random`. */
  random?: () => number;
}

/**
 * Full jitter: `random(0, min(cap, base * 2^step))`.
 *
 * Randomising the whole interval (rather than jittering around it) is what
 * actually spreads a synchronised herd — the AWS simulation shows backoff on
 * its own does not.
 */
function fullJitter(step: number, random: () => number): number {
  const ceiling = Math.min(REAPER_PARITY_MS, FIRST_REFRESH_AFTER_MS * BACKOFF_FACTOR ** step);
  return Math.round(random() * ceiling);
}

export function createRefreshEscalation(options: RefreshEscalationOptions = {}): RefreshEscalation {
  const random = options.random ?? Math.random;
  let step = 0;
  let exhausted = false;

  return {
    nextDelayMs: () => {
      if (exhausted) return null;
      return fullJitter(step, random);
    },

    onRefreshIssued: () => {
      if (exhausted) return false;
      step += 1;
      // The undelayed ceiling is what decides exhaustion, not the jittered
      // sample — otherwise a lucky low roll would keep a dead video alive.
      if (FIRST_REFRESH_AFTER_MS * BACKOFF_FACTOR ** step >= REAPER_PARITY_MS) {
        exhausted = true;
        return true;
      }
      return false;
    },

    onProgressObserved: () => {
      step = 0;
      exhausted = false;
    },

    isExhausted: () => exhausted,

    snapshot: () => ({ step, exhausted }),
  };
}

/**
 * Has this video been quiet long enough to justify a refresh?
 *
 * Deliberately takes both signals: a video mid-encode holds one status for
 * minutes while `bunny_encode_progress` climbs, and that is healthy.
 */
export function isQuietLongEnough(msSinceLastMovement: number): boolean {
  return msSinceLastMovement >= FIRST_REFRESH_AFTER_MS;
}

export const REFRESH_ESCALATION_BOUNDS = {
  FIRST_REFRESH_AFTER_MS,
  REAPER_PARITY_MS,
  BACKOFF_FACTOR,
} as const;
